import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { motion } from "framer-motion";
import { 
  Sparkles, 
  ArrowRight, 
  Activity, 
  Flame, 
  TrendingUp,
  CheckCircle,
  Target,
  Dumbbell,
  Apple,
  Calendar
} from "lucide-react";

const AthleteLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if already logged in
    const athleteToken = localStorage.getItem("habitia_athlete_token");
    if (athleteToken) {
      navigate("/athlete/dashboard");
    }
  }, [navigate]);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/athlete/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleDemoAccess = () => {
    setLoading(true);
    setTimeout(() => {
      localStorage.setItem("habitia_athlete_token", "athlete_demo_" + Date.now());
      navigate("/athlete/dashboard");
    }, 1000);
  };

  const features = [
    { icon: Target, text: "Configura tus propias metas de macros" },
    { icon: Apple, text: "Sube tu dieta o créala desde cero" },
    { icon: TrendingUp, text: "Seguimiento diario de tu progreso" },
    { icon: Flame, text: "Mantén tu racha y construye hábitos" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F4D40] via-[#134E3E] to-[#0A3A2E] overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          className="absolute top-1/4 -left-20 w-96 h-96 bg-[#178A6B]/20 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], x: [0, 30, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-1/4 -right-20 w-80 h-80 bg-green-500/15 rounded-full blur-3xl"
          animate={{ scale: [1, 1.3, 1], x: [0, -40, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className="flex flex-col items-center mb-8"
          >
            <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20 mb-4">
              <Activity className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white">
              Habit<span className="font-light">IA</span> <span className="text-green-400">Athlete</span>
            </h1>
            <p className="text-white/70 mt-2 text-center">
              Tu coach personal de nutrición con IA
            </p>
          </motion.div>

          {/* Main Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-6"
          >
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-white mb-2">
                Entrena solo, pero con guía
              </h2>
              <p className="text-white/60 text-sm">
                Sube tu dieta o crea una desde cero. La IA te ayuda a seguirla.
              </p>
            </div>

            {/* Features */}
            <div className="space-y-3 mb-6">
              {features.map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + i * 0.1 }}
                  className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-white/10"
                >
                  <div className="w-8 h-8 bg-[#178A6B]/20 rounded-lg flex items-center justify-center">
                    <feature.icon className="w-4 h-4 text-[#178A6B]" />
                  </div>
                  <span className="text-white/80 text-sm">{feature.text}</span>
                </motion.div>
              ))}
            </div>

            {/* Login Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleGoogleLogin}
                className="w-full rounded-xl bg-white text-[#0F4D40] hover:bg-white/90 py-5 font-semibold"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continuar con Google
              </Button>

              <Button
                onClick={handleDemoAccess}
                disabled={loading}
                variant="outline"
                className="w-full rounded-xl border-white/30 bg-white/5 text-white hover:bg-white/10 py-5"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    <Dumbbell className="w-5 h-5 mr-2" />
                    Probar Demo Gratis
                  </>
                )}
              </Button>
            </div>

            {/* Pricing */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="flex items-center justify-center gap-4">
                <div className="text-center">
                  <p className="text-white/50 text-xs">Basic</p>
                  <p className="text-white font-bold">$9<span className="text-white/50 text-sm">/mes</span></p>
                </div>
                <div className="text-center px-4 py-2 bg-[#178A6B]/20 rounded-xl border border-[#178A6B]/30">
                  <p className="text-[#178A6B] text-xs font-medium">Pro ⭐</p>
                  <p className="text-white font-bold">$19<span className="text-white/50 text-sm">/mes</span></p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Back to main */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-6 text-center"
          >
            <button
              onClick={() => navigate("/")}
              className="text-white/50 text-sm hover:text-white transition-colors"
            >
              ← ¿Eres nutricionista? Ir a HabitIA Pro
            </button>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default AthleteLogin;
